import { useState, useEffect, useRef } from "react";
import { Bell, Globe, Monitor, Moon, Sun, DollarSign, Calendar, LayoutDashboard, Handshake, Package, Search, MessageSquare, ArrowRightLeft } from "lucide-react";
import CustomSelect from "../../../components/ui/CustomSelect";
import api from "../../../api";
import { useAuth } from "../../../context/AuthProvider";
import { motion } from "framer-motion";

const Toggle = ({ enabled, onChange }) => (
  <motion.button
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none border border-workspace ${
      enabled ? "bg-success-500" : "bg-ink-300 dark:bg-ink-600"
    }`}
    onClick={() => onChange(!enabled)}
    whileTap={{ scale: 0.95 }}
  >
    <motion.span
      animate={{ x: enabled ? 22 : 2 }}
      transition={{ type: "spring", stiffness: 500, damping: 30 }}
      className="inline-block h-4 w-4 rounded-full bg-white shadow-sm border border-ink-200 dark:border-transparent"
    />
  </motion.button>
);

export default function Preferences() {
  const { user } = useAuth();
  
  const [darkMode, setDarkMode] = useState(document.documentElement.classList.contains("dark"));
  const [notifications, setNotifications] = useState(true);
  const [sound, setSound] = useState(false);
  const [language, setLanguage] = useState("fr");
  const [currency, setCurrency] = useState("EUR");
  const [density, setDensity] = useState("normal");
  const [homepage, setHomepage] = useState("dashboard");
  const [dateFormat, setDateFormat] = useState("dd/mm/yyyy");

  const isInitialMount = useRef(true);

  // Fetch preferences on mount
  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const res = await api.post("/utilisateurs/getUser");
        if (res.data.preferences) {
          const prefs = res.data.preferences;
          if (prefs.darkMode !== undefined) setDarkMode(prefs.darkMode);
          if (prefs.notifications !== undefined) setNotifications(prefs.notifications);
          if (prefs.sound !== undefined) setSound(prefs.sound);
          if (prefs.language !== undefined) setLanguage(prefs.language);
          if (prefs.currency !== undefined) setCurrency(prefs.currency);
          if (prefs.density !== undefined) setDensity(prefs.density);
          if (prefs.homepage !== undefined) setHomepage(prefs.homepage);
          if (prefs.dateFormat !== undefined) setDateFormat(prefs.dateFormat);
        }
      } catch (err) {
        console.error("Erreur de chargement des préférences", err);
      }
    };
    if (user) fetchPrefs();
  }, [user]);

  // Save preferences when they change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const savePrefs = async () => {
      try {
        await api.put("/utilisateurs/me/preferences", {
          preferences: {
            darkMode,
            notifications,
            sound,
            language,
            currency,
            density,
            homepage,
            dateFormat
          }
        });
      } catch (err) {
        console.error("Erreur de sauvegarde des préférences", err);
      }
    };
    
    // Apply visual side-effects
    document.documentElement.classList.toggle("dark", darkMode);
    document.documentElement.setAttribute("data-density", density);
    document.documentElement.lang = language;
    
    // Debounce or save directly
    const timeoutId = setTimeout(() => savePrefs(), 500);
    return () => clearTimeout(timeoutId);
  }, [darkMode, notifications, sound, language, currency, density, homepage, dateFormat]);

  return (
    <div className="p-6 h-full flex flex-col gap-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent">
      <div className="mb-2">
        <h2 className="text-2xl font-bold text-primary">Préférences</h2>
        <p className="text-sm text-subtle mt-1">Personnalisez votre expérience sur la plateforme Hipparchus.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section Interface & Affichage */}
        <div className="workspace-card p-6 flex flex-col gap-6">
          <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
            <Monitor className="w-5 h-5 text-info-500" />
            Interface & Affichage
          </h3>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-primary">Mode sombre</p>
              <p className="text-xs text-subtle">Adapter l'interface à votre environnement</p>
            </div>
            <div className="flex items-center gap-3">
              <Sun className="w-4 h-4 text-muted" />
              <Toggle enabled={darkMode} onChange={setDarkMode} />
              <Moon className="w-4 h-4 text-muted" />
            </div>
          </div>

          <div className="border-t border-workspace pt-4">
            <label className="block text-sm font-medium text-primary mb-2">Densité d'affichage</label>
            <div className="grid grid-cols-3 gap-2">
              {["compact", "normal", "spacious"].map((opt) => (
                <button
                  key={opt}
                  onClick={() => setDensity(opt)}
                  className={`px-3 py-2 text-sm rounded-lg border transition ${
                    density === opt 
                      ? "bg-primary-50 border-primary-500 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300" 
                      : "bg-surface border-workspace text-muted hover:border-ink-400"
                  }`}
                >
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-workspace pt-4">
            <label className="block text-sm font-medium text-primary mb-2">Page d'accueil par défaut</label>
            <CustomSelect
              value={homepage}
              onChange={setHomepage}
              options={[
                { value: "dashboard", label: <div className="flex items-center gap-2"><LayoutDashboard className="w-4 h-4 text-muted"/> Tableau de Bord</div> },
                { value: "partenaires", label: <div className="flex items-center gap-2"><Handshake className="w-4 h-4 text-muted"/> Partenaires</div> },
                { value: "produits", label: <div className="flex items-center gap-2"><Package className="w-4 h-4 text-muted"/> Produits</div> },
                { value: "prospects", label: <div className="flex items-center gap-2"><Search className="w-4 h-4 text-muted"/> Prospection</div> },
                { value: "calendrier", label: <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-muted"/> Calendrier</div> },
                { value: "forum", label: <div className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-muted"/> Forum</div> },
                { value: "transactions", label: <div className="flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-muted"/> Transactions</div> }
              ]}
              placeholder="Sélectionner une page"
            />
          </div>
        </div>

        {/* Section Régionalisation */}
        <div className="workspace-card p-6 flex flex-col gap-6">
          <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
            <Globe className="w-5 h-5 text-success-500" />
            Régionalisation
          </h3>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-primary">Langue de l'interface</label>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-[var(--hp-info-100)] text-[var(--hp-info-800)] px-2 py-0.5 rounded">Bientôt</span>
            </div>
            <CustomSelect
              value={language}
              onChange={setLanguage}
              options={[
                { value: "fr", label: <div className="flex items-center gap-2"><span className="text-base leading-none">🇫🇷</span> Français</div> },
                { value: "en", label: <div className="flex items-center gap-2"><span className="text-base leading-none">🇬🇧</span> Anglais</div> },
                { value: "es", label: <div className="flex items-center gap-2"><span className="text-base leading-none">🇪🇸</span> Espagnol</div> }
              ]}
              placeholder="Sélectionner une langue"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-primary flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-muted" /> Devise par défaut
              </label>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-[var(--hp-info-100)] text-[var(--hp-info-800)] px-2 py-0.5 rounded">Bientôt</span>
            </div>
            <CustomSelect
              value={currency}
              onChange={setCurrency}
              options={[
                { value: "EUR", label: "€ Euro (EUR)" },
                { value: "USD", label: "$ Dollar US (USD)" },
                { value: "CHF", label: "Fr Franc Suisse (CHF)" }
              ]}
              placeholder="Sélectionner une devise"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted" /> Format de date
            </label>
            <CustomSelect
              value={dateFormat}
              onChange={setDateFormat}
              options={[
                { value: "dd/mm/yyyy", label: "JJ/MM/AAAA (ex: 31/12/2026)" },
                { value: "mm/dd/yyyy", label: "MM/JJ/AAAA (ex: 12/31/2026)" },
                { value: "yyyy-mm-dd", label: "AAAA-MM-JJ (ex: 2026-12-31)" }
              ]}
              placeholder="Sélectionner un format"
            />
          </div>
        </div>

        {/* Section Notifications */}
        <div className="workspace-card p-6 flex flex-col gap-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
            <Bell className="w-5 h-5 text-orange-500" />
            Notifications & Alertes
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start justify-between p-4 border border-workspace rounded-lg">
              <div>
                <p className="text-sm font-medium text-primary">Notifications système</p>
                <p className="text-xs text-subtle mt-1">Alertes visuelles pour les événements importants</p>
              </div>
              <Toggle enabled={notifications} onChange={setNotifications} />
            </div>

            <div className="flex items-start justify-between p-4 border border-workspace rounded-lg">
              <div>
                <p className="text-sm font-medium text-primary">Effets sonores</p>
                <p className="text-xs text-subtle mt-1">Sons lors des succès ou erreurs critiques</p>
              </div>
              <Toggle enabled={sound} onChange={setSound} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
